import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Checkbox, message, InputRef } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { authAPI } from '../services/api';
import { setAuth } from '../services/authToken';
import { usePermission } from '../contexts/PermissionContext';
import { gray } from '../theme/tokens';
import { fmtTime, fmtDateWeekday } from '../utils/date';

const SAVED_ID_KEY = 'sappt_saved_username';
const AUTO_LOGIN_KEY = 'sappt_auto_login';

const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveId, setSaveId] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);
  const [time, setTime] = useState(new Date());
  const usernameRef = useRef<InputRef>(null);
  const passwordRef = useRef<InputRef>(null);
  const navigate = useNavigate();
  const { reload: reloadPermissions } = usePermission();

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(AUTO_LOGIN_KEY) === '1') setAutoLogin(true);
    const saved = localStorage.getItem(SAVED_ID_KEY);
    if (saved) {
      setSaveId(true);
      form.setFieldsValue({ username: saved });
      setTimeout(() => passwordRef.current?.focus(), 100);
    } else {
      setTimeout(() => usernameRef.current?.focus(), 100);
    }
  }, [form]);

  const onFinish = async (values: { username: string; password: string }) => {
    setLoading(true);
    try {
      const { data } = await authAPI.login(values.username, values.password, autoLogin);
      setAuth(data.access_token, data.username, autoLogin);
      if (saveId) {
        localStorage.setItem(SAVED_ID_KEY, values.username);
      } else {
        localStorage.removeItem(SAVED_ID_KEY);
      }
      if (autoLogin) {
        localStorage.setItem(AUTO_LOGIN_KEY, '1');
      } else {
        localStorage.removeItem(AUTO_LOGIN_KEY);
      }
      await reloadPermissions();
      message.success(`${data.username}님, 환영합니다.`);
      navigate('/');
    } catch {
      message.error('아이디 또는 비밀번호가 올바르지 않습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (d: Date) => fmtTime(d);
  /* 일자 표시 표준(YYYY-MM-DD) — 요일은 괄호로만 병기한다 */
  const formatDate = (d: Date) => fmtDateWeekday(d);

  return (
    <div className="login-page">
      {/* 좌측 패널 - 연구소 브랜딩 */}
      <div className="login-left">
        <div className="login-left-bg">
          {/* 떠다니는 육각형들 (벤젠고리) */}
          <svg className="login-hex-float login-hex-1" width="120" height="104" viewBox="0 0 120 104">
            <polygon points="60,2 116,28 116,76 60,102 4,76 4,28" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
            <polygon points="60,16 100,36 100,68 60,88 20,68 20,36" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          </svg>
          <svg className="login-hex-float login-hex-2" width="80" height="70" viewBox="0 0 80 70">
            <polygon points="40,2 77,20 77,52 40,68 3,52 3,20" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
          </svg>
          <svg className="login-hex-float login-hex-3" width="60" height="52" viewBox="0 0 60 52">
            <polygon points="30,2 57,15 57,39 30,50 3,39 3,15" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1" />
          </svg>
          <svg className="login-hex-float login-hex-4" width="100" height="87" viewBox="0 0 100 87">
            <polygon points="50,2 97,25 97,65 50,85 3,65 3,25" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1.5" />
          </svg>
          <svg className="login-hex-float login-hex-5" width="50" height="44" viewBox="0 0 50 44">
            <polygon points="25,2 48,13 48,33 25,42 2,33 2,13" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
          </svg>

          {/* 분자 구조 네트워크 */}
          <svg className="login-molecule-net" width="400" height="400" viewBox="0 0 400 400">
            {/* 노드 */}
            <circle className="login-mol-node login-mol-pulse-1" cx="80" cy="60" r="5" fill="rgba(165,180,252,0.5)" />
            <circle className="login-mol-node login-mol-pulse-2" cx="200" cy="40" r="4" fill="rgba(165,180,252,0.4)" />
            <circle className="login-mol-node login-mol-pulse-3" cx="320" cy="80" r="6" fill="rgba(165,180,252,0.35)" />
            <circle className="login-mol-node login-mol-pulse-1" cx="50" cy="180" r="4" fill="rgba(165,180,252,0.3)" />
            <circle className="login-mol-node login-mol-pulse-2" cx="160" cy="150" r="7" fill="rgba(165,180,252,0.5)" />
            <circle className="login-mol-node login-mol-pulse-3" cx="280" cy="200" r="5" fill="rgba(165,180,252,0.4)" />
            <circle className="login-mol-node login-mol-pulse-1" cx="360" cy="160" r="4" fill="rgba(165,180,252,0.3)" />
            <circle className="login-mol-node login-mol-pulse-2" cx="100" cy="300" r="5" fill="rgba(165,180,252,0.35)" />
            <circle className="login-mol-node login-mol-pulse-3" cx="240" cy="320" r="6" fill="rgba(165,180,252,0.45)" />
            <circle className="login-mol-node login-mol-pulse-1" cx="340" cy="300" r="4" fill="rgba(165,180,252,0.3)" />
            <circle className="login-mol-node login-mol-pulse-2" cx="180" cy="370" r="5" fill="rgba(165,180,252,0.35)" />
            {/* 연결선 */}
            <line className="login-mol-line login-mol-line-pulse-1" x1="80" y1="60" x2="200" y2="40" stroke="rgba(165,180,252,0.12)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-2" x1="200" y1="40" x2="320" y2="80" stroke="rgba(165,180,252,0.1)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-3" x1="80" y1="60" x2="160" y2="150" stroke="rgba(165,180,252,0.12)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-1" x1="160" y1="150" x2="280" y2="200" stroke="rgba(165,180,252,0.1)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-2" x1="50" y1="180" x2="160" y2="150" stroke="rgba(165,180,252,0.08)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-3" x1="280" y1="200" x2="360" y2="160" stroke="rgba(165,180,252,0.1)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-1" x1="320" y1="80" x2="360" y2="160" stroke="rgba(165,180,252,0.08)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-2" x1="160" y1="150" x2="100" y2="300" stroke="rgba(165,180,252,0.1)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-3" x1="280" y1="200" x2="240" y2="320" stroke="rgba(165,180,252,0.12)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-1" x1="240" y1="320" x2="340" y2="300" stroke="rgba(165,180,252,0.08)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-2" x1="100" y1="300" x2="180" y2="370" stroke="rgba(165,180,252,0.1)" strokeWidth="1" />
            <line className="login-mol-line login-mol-line-pulse-3" x1="240" y1="320" x2="180" y2="370" stroke="rgba(165,180,252,0.08)" strokeWidth="1" />
          </svg>

          {/* DNA 이중나선 */}
          <div className="login-dna-container">
            <svg className="login-dna" width="60" height="300" viewBox="0 0 60 300">
              {[0, 40, 80, 120, 160, 200, 240].map((y, i) => (
                <g key={i}>
                  <ellipse cx="30" cy={y + 20} rx="25" ry="6" fill="none" stroke="rgba(165,180,252,0.12)" strokeWidth="1.2" />
                  <circle cx={30 + 25 * Math.cos((i * Math.PI) / 3.5)} cy={y + 20} r="2.5" fill="rgba(129,140,248,0.25)" />
                  <circle cx={30 - 25 * Math.cos((i * Math.PI) / 3.5)} cy={y + 20} r="2.5" fill="rgba(99,102,241,0.2)" />
                </g>
              ))}
            </svg>
          </div>

          {/* 떠다니는 파티클 */}
          <div className="login-particle login-particle-1" />
          <div className="login-particle login-particle-2" />
          <div className="login-particle login-particle-3" />
          <div className="login-particle login-particle-4" />
          <div className="login-particle login-particle-5" />
          <div className="login-particle login-particle-6" />
        </div>

        <div className="login-left-content">
          <div className="login-badge">
            <span className="login-badge-dot" />
            <span>RESEARCH LAB ONLINE</span>
          </div>

          <h1 className="login-logo">SAPPT</h1>
          <div className="login-subtitle">
            Safety Assessment of Pesticide Protocol & Testing
          </div>

          <h2 className="login-headline">
            농약 시험계획서<br />통합 관리 시스템
          </h2>
          <p className="login-desc">
            약제방제 효과시험 계획서의 작성, 관리, HWPX 문서 자동 생성을
            하나의 플랫폼에서 수행할 수 있는 연구 지원 시스템입니다.
          </p>

          {/* 기능 카드 - 과학 테마 아이콘 */}
          <div className="login-features">
            {[
              { icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(165,180,252,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3h6v8l4 7H5l4-7V3z" /><line x1="9" y1="3" x2="15" y2="3" /><line x1="10" y1="9" x2="14" y2="9" />
                </svg>
              ), label: '시험 설계', desc: '프로토콜 기반' },
              { icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(165,180,252,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="15" x2="15" y2="15" /><line x1="9" y1="11" x2="13" y2="11" />
                </svg>
              ), label: 'HWPX 변환', desc: '자동 문서 생성' },
              { icon: (
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(165,180,252,0.9)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                  <circle cx="12" cy="12" r="2" fill="rgba(165,180,252,0.4)" />
                </svg>
              ), label: '일정 관리', desc: '처리/조사 추적' },
            ].map((item, i) => (
              <div key={i} className="login-feature-card">
                <div className="login-feature-icon">{item.icon}</div>
                <div className="login-feature-label">{item.label}</div>
                <div className="login-feature-desc">{item.desc}</div>
              </div>
            ))}
          </div>

          {/* SAPPT Domains */}
          <div className="login-domains">
            <div className="login-domains-title">SAPPT Domains</div>
            <div className="login-domains-grid">
              {[
                { label: 'Admin', domain: 'lab.sappt.co.kr', desc: '관리자 시스템' },
                { label: 'Partner', domain: 'partner.sappt.co.kr', desc: '파트너 포털' },
              ].map((item, i) => (
                <div key={i} className="login-domain-item">
                  <span className="login-domain-label">{item.label}</span>
                  <span className="login-domain-url">{item.domain}</span>
                  <span className="login-domain-desc">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="login-footer-info">
            <div>
              <div className="login-clock">{formatTime(time)}</div>
              <div className="login-date">{formatDate(time)}</div>
            </div>
            <div className="login-footer-divider" />
            <div className="login-domain-current">lab.sappt.co.kr</div>
          </div>
        </div>
      </div>

      {/* 우측 패널 - 로그인 폼 */}
      <div className="login-right">
        <div className="login-form-card">
          <div style={{ marginBottom: 36 }}>
            <div className="login-form-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4f46e5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="10" r="3" />
                <path d="M12 2a8 8 0 0 0-8 8c0 5.4 7 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z" />
              </svg>
            </div>
            <h3 className="login-form-title">연구실 로그인</h3>
            <p className="login-form-subtitle">인증된 연구원만 접근할 수 있습니다</p>
          </div>

          <Form form={form} onFinish={onFinish} size="large" layout="vertical">
            <Form.Item
              name="username"
              label={<span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>아이디</span>}
              rules={[{ required: true, message: '아이디를 입력하세요' }]}
              style={{ marginBottom: 20 }}
            >
              <Input
                ref={usernameRef}
                prefix={<UserOutlined style={{ color: gray[400] }} />}
                placeholder="아이디를 입력하세요"
                className="login-input"
              />
            </Form.Item>
            <Form.Item
              name="password"
              label={<span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>비밀번호</span>}
              rules={[{ required: true, message: '비밀번호를 입력하세요' }]}
              style={{ marginBottom: 16 }}
            >
              <Input.Password
                ref={passwordRef}
                prefix={<LockOutlined style={{ color: gray[400] }} />}
                placeholder="비밀번호를 입력하세요"
                className="login-input"
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Checkbox
                  checked={saveId}
                  onChange={e => setSaveId(e.target.checked)}
                  style={{ fontSize: 13, color: gray[500] }}
                >
                  아이디 저장
                </Checkbox>
                <Checkbox
                  checked={autoLogin}
                  onChange={e => setAutoLogin(e.target.checked)}
                  style={{ fontSize: 13, color: gray[500] }}
                >
                  자동로그인
                </Checkbox>
              </div>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={loading} block className="login-btn">
                {loading ? '인증 중...' : '로그인'}
              </Button>
            </Form.Item>
          </Form>

          <div className="login-security">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>SSL/TLS 암호화 통신으로 보호됩니다</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
